import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type InsuranceType =
  | "general_liability"
  | "workers_comp"
  | "umbrella"
  | "commercial_auto"
  | "professional_liability"
  | "builders_risk"
  | "other";

export interface VendorInsurance {
  id: string;
  company_id: string;
  vendor_id: string;
  insurance_type: InsuranceType;
  insurance_type_other: string | null;
  carrier_name: string | null;
  policy_number: string | null;
  agent_name: string | null;
  agent_phone: string | null;
  agent_email: string | null;
  per_occurrence_limit: number | null;
  aggregate_limit: number | null;
  effective_date: string | null;
  expiry_date: string | null;
  is_active: boolean;
  document_path: string | null;
  document_name: string | null;
  uploaded_at: string | null;
  uploaded_by: string | null;
  company_additional_insured: boolean;
  additional_insured_doc_path: string | null;
  notes: string | null;
  created_at: string;
}

export interface CreateInsuranceInput {
  vendor_id: string;
  insurance_type: InsuranceType;
  insurance_type_other?: string;
  carrier_name?: string;
  policy_number?: string;
  agent_name?: string;
  agent_phone?: string;
  agent_email?: string;
  per_occurrence_limit?: number;
  aggregate_limit?: number;
  effective_date?: string;
  expiry_date?: string;
  company_additional_insured?: boolean;
  notes?: string;
}

export const useVendorInsurance = (vendorId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["vendor_insurance", vendorId],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("vendor_insurance") as any)
        .select("*")
        .eq("vendor_id", vendorId!)
        .eq("is_active", true)
        .order("created_at");
      if (error) throw error;
      return (data ?? []) as VendorInsurance[];
    },
    enabled: !!user && !!vendorId,
  });
};

export const useCreateVendorInsurance = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateInsuranceInput) => {
      const { data: companyId, error: cidErr } = await supabase.rpc("my_company_id");
      if (cidErr) throw cidErr;
      const { data, error } = await (supabase
        .from("vendor_insurance") as any)
        .insert({
          company_id: companyId,
          vendor_id: input.vendor_id,
          insurance_type: input.insurance_type,
          insurance_type_other: input.insurance_type_other || null,
          carrier_name: input.carrier_name || null,
          policy_number: input.policy_number || null,
          agent_name: input.agent_name || null,
          agent_phone: input.agent_phone || null,
          agent_email: input.agent_email || null,
          per_occurrence_limit: input.per_occurrence_limit ?? null,
          aggregate_limit: input.aggregate_limit ?? null,
          effective_date: input.effective_date || null,
          expiry_date: input.expiry_date || null,
          company_additional_insured: input.company_additional_insured ?? false,
          notes: input.notes || null,
        })
        .select("id")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["vendor_insurance", vars.vendor_id] });
    },
  });
};

export const useUploadInsuranceCOI = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ insuranceId, vendorId, file }: { insuranceId: string; vendorId: string; file: File }) => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      const filePath = `${companyId}/${vendorId}/insurance/${Date.now()}_${file.name}`;
      const { error: uploadErr } = await supabase.storage.from("vendor-docs").upload(filePath, file);
      if (uploadErr) throw uploadErr;
      const { error } = await (supabase
        .from("vendor_insurance") as any)
        .update({
          document_path: filePath,
          document_name: file.name,
          uploaded_at: new Date().toISOString(),
        })
        .eq("id", insuranceId);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["vendor_insurance", vars.vendorId] });
    },
  });
};
